/**
 * Atlas PM · P6.2 · 新建任务抽屉（全局容器）
 *
 * 与任务详情抽屉（features/detail/TaskDrawer.tsx）**同一种浮层方式**：
 *   - 右侧滑入，宽度 min(1200px, 92%)（比详情的 980px 宽，因设计器内容更多）
 *   - 左侧 2px 渐变光条（cyan → violet）
 *   - 顶部标题 + 关闭按钮（触摸目标 ≥ 44px）
 *   - Esc 可关闭
 *
 * ⚠️ 不复用详情抽屉的 `#drawer`（id 选择器，HTML 内 id 必须唯一）；
 *    这里用独立类 `.td-drawer`，只对齐关键视觉，避免改动 detail.css 影响详情。
 *
 * 挂在 AppShell 里（与 <TaskDrawer /> 并列），读 useUiStore().taskFormOpen。
 */

import { useEffect, useState } from 'react';
import { useUiStore } from '../../stores/ui';
import TaskDesigner from './TaskDesigner';
import './task-form.css';

export default function TaskFormDrawer() {
  const open = useUiStore((s) => s.taskFormOpen);
  const projectId = useUiStore((s) => s.taskFormProjectId);
  const editTaskId = useUiStore((s) => s.taskFormEditId);
  const closeTaskForm = useUiStore((s) => s.closeTaskForm);

  /* ⭐ P7.14：主操作按钮（创建/保存）仍由 TaskDesigner 持有，
     通过 portal 投递到标题行右侧的挂载点，避免跨组件状态提升。 */
  const [actionsTarget, setActionsTarget] = useState<HTMLDivElement | null>(null);

  /* ⭐ P7.5：编辑模式（editTaskId 非空）复用同一抽屉 */
  const isEdit = Boolean(editTaskId);
  const title = isEdit ? '编辑任务' : '新建任务';

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTaskForm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeTaskForm]);

  if (!open) return null;

  return (
    <aside
      id="task-form-drawer"
      className="td-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="task-form-drawer"
      data-mode={isEdit ? 'edit' : 'create'}
    >
      <header className="td-drawer__head">
        <h2 className="td-drawer__title">{title}</h2>
        {/* ⭐ P7.14：主操作按钮挂载点（TaskDesigner 通过 portal 投递） */}
        <div className="td-drawer__actions" ref={setActionsTarget} />
        <button
          type="button"
          className="td-drawer__close"
          onClick={closeTaskForm}
          aria-label={isEdit ? '关闭编辑任务' : '关闭新建任务'}
          data-testid="task-form-close"
        >
          ✕
        </button>
      </header>

      <div className="td-drawer__body">
        <TaskDesigner projectId={projectId} editTaskId={editTaskId} actionsTarget={actionsTarget} />
      </div>
    </aside>
  );
}
